// Mailpage.tsx

import { IconLoader, IconSend } from "@tabler/icons-react";
import React from "react";
import { useForm } from "react-hook-form";
import { useEmployeeAPI } from "src/API/rest.employee";
import { useForecastMailCC } from "src/API/rest.mailsend";
import { ButtonIconSquare } from "src/components/buttons";
import { MultiselectStandard } from "src/components/standard.multiselect";
import { TypographyH2 } from "src/components/typography";
import { toast } from "src/shadcn/ui/use-toast";
// ... other imports

type FormData = {
    alert_cc_users_ids: number[];
    intimation_cc_users_ids: number[];
};

export default function Mailpage() {
    const {
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
        formState: { errors },
    } = useForm<FormData>({
        defaultValues: {
            alert_cc_users_ids: [],
            intimation_cc_users_ids: [],
        },
    });

    const { emloyees, isLoading: loadingEmployees } = useEmployeeAPI({
        page_size: 1000,
        page: 1,
        trigger: true,
    });

    const {
        currentConfig,
        isLoadingConfig,
        sendMailCC,
        isSending,
        isSuccess,
        isError,
        error,
        reset: resetMutation,
    } = useForecastMailCC();

    const employeeOptions =
        emloyees?.map((emp) => ({
            value: String(emp.FM03_EmployeeID),
            label: `${emp.FM03_EmployeeName} (${emp.FM03_EmployeeID})`,
        })) || [];

    // Pre-fill form when data is loaded
    React.useEffect(() => {
        if (currentConfig) {
            const alertIds = currentConfig.alert_cc_users.map(u => u.FM03_EmployeeID);
            const intimationIds = currentConfig.intimation_cc_users.map(u => u.FM03_EmployeeID);

            setValue("alert_cc_users_ids", alertIds);
            setValue("intimation_cc_users_ids", intimationIds);
        }
    }, [currentConfig, setValue]);

    const onSubmit = async (data: FormData) => {
        try {
            await sendMailCC({
                alert_cc_users_ids: data.alert_cc_users_ids,
                intimation_cc_users_ids: data.intimation_cc_users_ids,
            });

            toast({
                description: "Mail CC users updated successfully!",
            });
        } catch (err: any) {
            toast({
                title: "Error",
                description: err.message || "Failed to update mail CC users",
                variant: "destructive",
            });
        }
    };

    React.useEffect(() => {
        if (isSuccess) {
            const timer = setTimeout(() => resetMutation(), 3000);
            return () => clearTimeout(timer);
        }
    }, [isSuccess, resetMutation]);

    const isLoading = loadingEmployees || isLoadingConfig || isSending;

    return (
        <div className="grow overflow-auto rounded-xl h-full w-full bg-vstargray-100/60 dark:bg-vstargray-900 p-3 sm:p-7 flex flex-col gap-6">
            <div className="flex justify-center items-center w-full">
                <TypographyH2>Forecast Mail CC Configuration</TypographyH2>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="max-w-2xl mx-auto w-full space-y-8">
                {/* Alert CC Users */}
                <MultiselectStandard
                    title="Alert CC Users"
                    placeholder="Select employees for Alert CC"
                    options={employeeOptions}
                    value={watch("alert_cc_users_ids")?.map(String) || []}
                    onValueChange={(e) => {
                        const vals = (e.target.value || []).map(Number);
                        setValue("alert_cc_users_ids", vals);
                    }}
                    disabled={isLoading}
                    {...register("alert_cc_users_ids")}
                    error={errors.alert_cc_users_ids?.message}
                />

                {/* Intimation CC Users */}
                <MultiselectStandard
                    title="Intimation CC Users"
                    placeholder="Select employees for Intimation CC"
                    options={employeeOptions}
                    value={watch("intimation_cc_users_ids")?.map(String) || []}
                    onValueChange={(e) => {
                        const vals = (e.target.value || []).map(Number);
                        setValue("intimation_cc_users_ids", vals);
                    }}
                    disabled={isLoading}
                    {...register("intimation_cc_users_ids")}
                    error={errors.intimation_cc_users_ids?.message}
                />

                {/* Submit Button */}
                <div className="flex justify-end pt-6">
                    <ButtonIconSquare
                        type="submit"
                        variant="secondary"
                        icon={isSending ? <IconLoader className="animate-spin" /> : <IconSend />}
                        text={isSending ? "Sending..." : "Update Mail CC"}
                        disabled={isLoading}
                    />
                </div>

                {/* Status Messages */}
                {isError && (
                    <div className="text-red-500 text-sm">
                        Error: {(error as any)?.message || "Something went wrong"}
                    </div>
                )}
                {isSuccess && (
                    <div className="text-green-600 text-sm font-medium">
                        Successfully updated!
                    </div>
                )}
            </form>
        </div>
    );
}